import AuthorizationService from '@/services/AuthorizationService';
import { logDebug } from '@/utils/logger';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
interface AuthorizationContextType {
  isUserAuthorized: boolean;
  setUnauthorized: () => void;
  getAuthorizationStatus: () => boolean;
}
const DEFAULT_CONTEXT: AuthorizationContextType = {
  isUserAuthorized: true,
  setUnauthorized: () => {},
  getAuthorizationStatus: () => true,
};
const AuthorizationContext =
  createContext<AuthorizationContextType>(DEFAULT_CONTEXT);
interface AuthorizationProviderProps {
  children: ReactNode;
}
export const AuthorizationProvider = ({
  children,
}: AuthorizationProviderProps): JSX.Element => {
  const [isUserAuthorized, setIsUserAuthorized] = useState<boolean>(
    AuthorizationService.getAuthorizationStatus(),
  );
  const setUnauthorized = useCallback(() => {
    AuthorizationService.setUnauthorized();
  }, []);
  const getAuthorizationStatus = useCallback(() => {
    return AuthorizationService.getAuthorizationStatus();
  }, []);
  useEffect(() => {
    logDebug(
      `Initializing authorization subscription with status: ${AuthorizationService.getAuthorizationStatus()}`,
      'AuthorizationProvider.useEffect',
    );
    const unsubscribe = AuthorizationService.subscribe((newStatus) => {
      setIsUserAuthorized(newStatus);
      logDebug(
        `Authorization status updated to: ${newStatus}`,
        'AuthorizationProvider.statusSubscriber',
      );
    });
    return unsubscribe;
  }, []);
  const contextValue = {
    isUserAuthorized,
    setUnauthorized,
    getAuthorizationStatus,
  };
  return (
    <AuthorizationContext.Provider value={contextValue}>
      {children}
    </AuthorizationContext.Provider>
  );
};
export const useAuthorization = (): AuthorizationContextType => {
  return useContext(AuthorizationContext);
};
